import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import Input from "@material-ui/core/Input";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import { fetchAddressingKeys } from "redux/actions/invoices";
import { FETCH_ADDRESSING_KEYS } from "redux/constants";

/**
 * Clé d'adressage (Billit, lot 2) : le suffixe que le fournisseur ajoute à l'adresse électronique
 * d'EPSA (537976631_DP ⇒ « DP »). Elle sert uniquement au tri : une étiquette et un filtre.
 *
 * La facture ne porte que le CODE ; le libellé vient de la table de référence (`GET /addressing-keys`),
 * pour qu'un libellé changé une fois s'applique à tous les documents.
 */
const KEY_COLOR = "#3C4D6F";

export const useAddressingKeys = () =>
	useSelector(({ requests }) => get(requests, `queries.${FETCH_ADDRESSING_KEYS}.data`)) || [];

// Code absent de la table (chargée avant l'arrivée de la facture, ou pas encore chargée) ⇒ le code.
// Comparaison sans la casse, comme la base (collation `_ci`) : « dp » arrivé après « DP » n'a pas sa
// propre ligne dans la table, et le filtre « DP » le trouve déjà.
const sameCode = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

export const addressingKeyLabel = (keys, code) =>
	get(
		keys.find((key) => sameCode(key.code, code)),
		"label"
	) || code;

/** Étiquette de la clé. Pas de clé (papier, mail, électronique sans suffixe) ⇒ rien, pas de puce vide. */
export function AddressingKeyChip({ code, size = "small", style }) {
	const { t } = useTranslation();
	const keys = useAddressingKeys();
	if (!code) return null;
	return (
		<Tooltip placement="top" title={t("addressingKey")}>
			<Chip
				data-testid="addressing-key-chip"
				label={addressingKeyLabel(keys, code)}
				size={size}
				variant="outlined"
				style={{ borderColor: KEY_COLOR, color: KEY_COLOR, ...style }}
			/>
		</Tooltip>
	);
}

/**
 * Filtre « Clé d'adressage », choix multiple sur le modèle du statut : choix = libellés, valeurs = codes.
 * Rechargé à l'ouverture, pour qu'une clé arrivée depuis l'affichage de la liste y figure.
 */
export function AddressingKeyFilter({ value, onChange }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const keys = useAddressingKeys();
	const selected = Array.isArray(value) ? value : [];
	return (
		<FormControl fullWidth disabled={isEmpty(keys) && isEmpty(selected)}>
			<InputLabel id="addressingKey">{t("addressingKey")}</InputLabel>
			<Select
				labelId="addressingKey"
				id="addressingKeyId"
				multiple
				value={selected}
				onOpen={() => dispatch(fetchAddressingKeys())}
				onChange={(event) => onChange(event.target.value)}
				input={<Input />}
				renderValue={(codes) => codes.map((code) => addressingKeyLabel(keys, code)).join(", ")}
			>
				{keys.map((key) => (
					<MenuItem key={key.code} value={key.code}>
						<Checkbox checked={selected.includes(key.code)} />
						<ListItemText primary={key.label} />
					</MenuItem>
				))}
			</Select>
		</FormControl>
	);
}
